<template>
  <div
    class="flex min-h-screen min-w-[300px] flex-col antialiased"
    :style="{
      minHeight: store.pageHeight > 0 ? `${store.pageHeight}px` : 'auto',
    }"
  >
    <!-- Google Tag Manager (noscript) -->
    <noscript>
      <iframe
          :src="`https://www.googletagmanager.com/ns.html?id=${gtmId}`"
          height="0"
          width="0"
          style="display: none; visibility: hidden"
      >
      </iframe>
    </noscript>
    <a
      href="#main-content"
      class="focus:ring-blue-500 sr-only absolute left-0 top-0 z-50 m-2 rounded bg-white p-2 text-black shadow-md transition duration-300 focus:not-sr-only focus:outline-none focus:ring-2"
    >
      {{ $t('global.skip_to_main_content') }}
    </a>
    <div class="z-0 overflow-x-hidden sm:sticky sm:top-0 sm:z-50">
      <Intersect
        :threshold="0.5"
        @enter.once="handleVisibility(bannerData.content)"
      >
        <Banner
          v-if="bannerData && !isCheckout"
          :blok="{
            ...bannerData.content,
            published_at: bannerData.published_at || '',
          }"
        />
      </Intersect>
    </div>
    <ClientOnly>
      <FAB v-if="!isCheckoutPage" />
    </ClientOnly>

    <Header
      ref="header"
      :fetching-categories="rootCategoriesPending"
      :root-categories="rootCategories?.categories"
      :blok="navigationData?.content"
      :force-minified="isCheckout"
      :stick-to-banner="!isMobile && !isCheckout"
    >
      <div
        v-if="!(isMobile && isCheckoutPage && showCheckoutPageNewLayout)"
        class="relative z-40"
        :class="{
          '!absolute inset-x-0': isPdp && isMobile,
          'pointer-events-none': !isMobileSearchBarVisible,
        }"
      >
        <HeaderSearch
          class="bg-white px-3 pb-2 transition-all md:hidden"
          :class="{
            '!-translate-y-full opacity-0': !isMobileSearchBarVisible,
          }"
          width="full"
          @change:search-results-visibility="
            handleMobileSearchResultsVisibilityChange
          "
        />
      </div>
    </Header>

    <ToastContainer />

    <MobileNavigation
      v-if="isMobileOrTablet"
      :fetching="rootCategoriesPending"
      :root-categories="rootCategories?.categories"
      :blok="cmsMainNavigationDataContent"
    />
    <div id="main-content" class="container z-10 grow bg-white xl:mt-2">
      <AuthErrorToastContainer />
      <slot
        keep-alive
        :keep-alive-props="{ include: ['index', 'product-slug'] }"
      />
      <ClientOnly>
        <LanguageSwitchModal />
      </ClientOnly>
      <div class="backdrop-target" />
    </div>
    <div class="modal-target" />
    <AgsLazy ssr placeholder-ratio="16/9">
      <template #lazy="{ visible }">
        <Footer
          v-if="visible && footerData?.content"
          class="mt-12"
          :hide-benefits="isBlog"
          :blok="footerData.content"
        />
      </template>
    </AgsLazy>
    <div class="filter-slide-in-target" />
    <div class="selection-modal-target" />
    <BackInStockModal v-if="showBackInStock" />
    <AddToBasketPopover v-if="isAddToBasketFlyoutOpen" />
  </div>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { ref, computed, onMounted, watch } from 'vue'
import { useEventListener, useDebounceFn } from '@vueuse/core'
import useExperiments from '~/composables/useExperiments'
import { getReferenceKeyForTrbo } from '~/utils/tracking'
import { toFloat } from '~/utils/currency'
import { useCurrentShop } from '#storefront/composables'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { fetchOnNavigation } from '~/utils/loading'
import useCountryRedirect from '~/composables/useCountryRedirect'
import useGlobalConfig from '~/composables/useGlobalConfig'
import useAgsWishlist from '~/composables/useAgsWishlist'
import useCategoryIcons from '~/composables/useCategoryIcons'
import useSmartShopConfiguration from '~/composables/useSmartShopConfiguration'
import useAgsBasket from '~/composables/useAgsBasket'
import useRootCategories from '~/composables/useRootCategories'
import useCms from '~/composables/cms/useCms'
import useZoomGalleryActions from '~/composables/ui/useZoomGalleryActions'
import { useDeviceType } from '~/composables/useDeviceType'
import { useRouter, useRoute } from '#app/composables/router'
import { useNuxtApp } from '#app/nuxt'
import useStore from '~/composables/useStore'
import useRouteHelpers from '~/composables/useRouteHelpers'
import useUiState from '~/composables/ui/useUiState'
import useExperimentTracking from '~/composables/useExperimentTracking'
import useParameterMemory from '~/composables/useParameterMemory'
import type {
  SbBanner,
  SbFooter,
  SbNavigation,
} from '~/cms/storyblok/types/storyblok.gen'
import addUserCentricsScript from '~/composables/helpers/userCentrics'
import useBackInStock from '~/composables/useBackInStock'
import useAgsUser from '~/composables/useAgsUser'
import { usePromotionController } from '~/composables/usePromotionController'
import useDefaultLayoutTracking from '~/composables/useDefaultLayoutTracking'

useParameterMemory('utm_')
useExperimentTracking()

const {
  isSideNavigationOpen,
  selectSideNavigationEntry,
  isProductSizeGuideModalOpen,
  isShippingHintCostModalOpen,
  isMobileSearchBarVisible,
  isPdp,
  showBackInStock,
  isAddToBasketFlyoutOpen,
} = useUiState()

useBackInStock()

const { toLocalePath } = useRouteHelpers()
const { usercentricsSettingsId, locale, isoLanguageCode, country } =
  useCurrentShop().value
const fullLocale = `${isoLanguageCode}-${country === 'UK' ? 'GB' : country}`
const store = useStore()
const { $i18n } = useNuxtApp()

const route = useRoute()
const router = useRouter()
const gtmId = process.env.NUXT_GTM_ID

const rootCategoriesPending = computed(
  () => rootCategoriesStatus.value === 'pending',
)

const { showCheckoutPageNewLayout, loadLeapScript } = useExperiments()
const isCheckoutPage = computed(() => route.fullPath.includes('checkout'))

let lastScrollTop = 0

function hideOrShowSearchBar() {
  if (isPdp.value && isMobile.value) {
    return
  }
  const scrollTop = document?.documentElement?.scrollTop
  const diff = Math.abs(scrollTop - lastScrollTop)
  if (diff <= 10 && scrollTop > 100) {
    return
  }
  isMobileSearchBarVisible.value = scrollTop < 100 || scrollTop <= lastScrollTop
  lastScrollTop = scrollTop
}

useEventListener('scroll', useDebounceFn(hideOrShowSearchBar, 10))

watch(() => route.fullPath, hideOrShowSearchBar)

onMounted(() => {
  if (usercentricsSettingsId) {
    addUserCentricsScript(isoLanguageCode, usercentricsSettingsId)
  }
  trackTrboBasket(trboBasketPayload.value)
  hideOrShowSearchBar()
})

const { isMobileOrTablet, isMobile } = useDeviceType()
const isCheckout = computed(() => route.name?.toString().includes('checkout'))
const isBlog = computed(() =>
  route.path.includes(toLocalePath({ name: 'magazine' }).toString()),
)

const areMobileSearchResultsVisible = ref(false)
const { state: zoomGallery } = useZoomGalleryActions()
const handleMobileSearchResultsVisibilityChange = (areVisible: boolean) => {
  areMobileSearchResultsVisible.value = areVisible
}

const { trackPromotion, trackPromotionView } = useTrackingEvents()

// CMS
const { fetchBySlug: fetchBanner, data: bannerData } =
  useCms<SbBanner>('banner')
const { fetchBySlug: fetchFooter, data: footerData } =
  useCms<SbFooter>('footer')
const { fetchBySlug: fetchNavigation, data: navigationData } =
  useCms<SbNavigation>('main-navigation')

const { data: rootCategories, status: rootCategoriesStatus } =
  useRootCategories({
    params: { children: NAVIGATION_DEPTH },
  })
const basket = useAgsBasket()
const { isLoggedIn, refresh } = useAgsUser()
const { fetch: fetchSmartShopConfiguration } = useSmartShopConfiguration()
const { fetch: fetchCategoryIcons } = useCategoryIcons()
const { fetch: fetchWishlist } = useAgsWishlist()
const { fetch: fetchGlobalConfig, runKameleoon } = useGlobalConfig()
const { refresh: fetchCountryRedirect } = useCountryRedirect({
  options: { immediate: false },
})
const { load: loadHtfScripts } = useHtfScripts()
useBackInStock()
usePromotionController({ setup: true })
useDefaultLayoutTracking(rootCategories)

const trackBannerPromotionView = () => {
  trackPromotionView({
    id: bannerData.value.content.promotion_id || '',
    name: bannerData.value.content.promotion_name || '',
    pageType: 'all',
    promoType: 'headerbanner',
    position: 1,
    creativeNameSb: bannerData.value.content.creative_name || '',
  })
}

onMounted(async () => {
  if (!isLoggedIn.value) {
    await refresh()
  }

  trackBannerPromotionView()
})

const fetchData = async () => {
  try {
    await Promise.all([
      fetchNavigation('main-navigation'),
      fetchBanner('banner'),
      fetchFooter('footer'),
      fetchSmartShopConfiguration(),
      fetchCategoryIcons(),
      fetchGlobalConfig(),
      fetchCountryRedirect(),
      ...(import.meta.client
        ? [fetchWishlist(), basket.fetch(), refresh()]
        : []),
    ])
  } catch (error) {
    console.error('Error fetching navigation:', error)
  }
}

const { trackShopInit, trackTrboBasket } = useTrackingEvents()

await fetchOnNavigation(fetchData)

trackShopInit()

const trboBasketPayload = computed(() => ({
  basketValue: toFloat(basket.data.value?.cost.withTax || 0),
  basketProductIdsArray:
    basket.data.value?.items
      ?.map((i: any) =>
        getReferenceKeyForTrbo(i.variant.referenceKey as string),
      )
      .filter((i: any) => Boolean(i)) || [],
}))

const handleVisibility = (banner: SbBanner) => {
  trackPromotion(
    'view_promotion',
    {
      promotion_name: banner.promotion_name || 'black_banner',
      location_id: banner.location_id || '',
      promotion_id: banner.promotion_id || '1',
      creative_slot: banner.creative_slot || '',
      creative_name: banner.creative_name || '',
    },
    {
      url: route.fullPath || '',
    },
  )
}

router.afterEach(() => {
  if (import.meta.server) return

  trackBannerPromotionView()

  // reset side navigation when leaving a product page or PLP
  if (
    !route.name?.toString().includes('all') &&
    !route.name?.toString().includes('p-slug-id')
  ) {
    selectSideNavigationEntry(undefined)
  }

  trackTrboBasket(trboBasketPayload.value)
})
// END TRACKING

if (
  typeof sessionStorage !== 'undefined' &&
  !sessionStorage.getItem('referrer')
) {
  sessionStorage.setItem('referrer', document.referrer)
}

const cmsMainNavigationDataContent = computed(
  () => navigationData.value?.content,
)

loadHtfScripts()
useHead(() => ({
  titleTemplate(title) {
    return title
      ? $i18n.t('global.title_template', { title })
      : $i18n.t('global.title_default')
  },
  script: [
    {
      id: 'gtm-inline-script',
      innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        '${process.env.NUXT_GTM_URL}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${process.env.NUXT_GTM_ID}');`,
      type: 'text/javascript',
      tagPriority: 'high',
      charset: 'utf-8',
    },
    ...(loadLeapScript.value && runKameleoon.value
      ? [
          {
            innerHTML: `
                  // Duration in milliseconds to wait while the Kameleoon application file is loaded
                  var kameleoonLoadingTimeout = 1000;
                  window.kameleoonQueue = window.kameleoonQueue || [];
                  window.kameleoonStartLoadTime = new Date().getTime();
                  if (!document.getElementById("kameleoonLoadingStyleSheet") && !window.kameleoonDisplayPageTimeOut) {
                    var kameleoonS = document.getElementsByTagName("script")[0];
                    var kameleoonCc = "* { visibility: hidden !important; background-image: none !important; }";
                    var kameleoonStn = document.createElement("style");
                    kameleoonStn.type = "text/css";
                    kameleoonStn.id = "kameleoonLoadingStyleSheet";
                    if (kameleoonStn.styleSheet) {
                      kameleoonStn.styleSheet.cssText = kameleoonCc;
                    } else {
                      kameleoonStn.appendChild(document.createTextNode(kameleoonCc));
                    }
                    kameleoonS.parentNode.insertBefore(kameleoonStn, kameleoonS);
                    window.kameleoonDisplayPage = function(fromEngine) {
                      if (!fromEngine) {
                        window.kameleoonTimeout = true;
                      }
                      if (kameleoonStn.parentNode) {
                        kameleoonStn.parentNode.removeChild(kameleoonStn);
                      }
                    };
                    window.kameleoonDisplayPageTimeOut = window.setTimeout(window.kameleoonDisplayPage, kameleoonLoadingTimeout);
                  }
                `,
            type: 'text/javascript',
            tagPriority: 'high' as const,
          },
          {
            src: '//bj3yocjsex.kameleoon.io/kameleoon.js',
            async: true,
            tagPriority: 'low' as const,
            fetchpriority: 'high' as const,
          },
        ]
      : []),
  ],
  __dangerouslyDisableSanitizersByTagID: {
    'gtm-inline-script': ['innerHTML'],
  },
  bodyAttrs: {
    /** HIDE SCROLLBAR WHILE MODALS OR NAVIGATION SLIDE ARE OPEN */
    class:
      showBackInStock.value ||
      isSideNavigationOpen.value ||
      isProductSizeGuideModalOpen.value ||
      isShippingHintCostModalOpen.value ||
      zoomGallery?.display
        ? 'overflow-hidden'
        : '',
  },
  htmlAttrs: {
    lang: fullLocale,
  },
  link: [
    { rel: 'preconnect', href: '//a.storyblok.com' },
    { rel: 'preconnect', href: '//app.storyblok.com' },
    {
      rel: 'preconnect',
      href: '//api.usercentrics.eu',
    },
    {
      rel: 'preconnect',
      href: '//app.usercentrics.eu',
    },
    {
      rel: 'dns-prefetch',
      href: '//api.usercentrics.eu',
    },
    { rel: 'dns-prefetch', href: '//app.usercentrics.eu' },
    {
      rel: 'preload',
      href: 'https://web.cmp.usercentrics.eu/ui/loader.js',
      as: 'script',
    },
  ],
  meta: [
    ...(process.env.GOOGLE_SITE_VERIF
      ? [
          {
            name: 'google-site-verification',
            content: process.env.GOOGLE_SITE_VERIF,
          },
        ]
      : []),
  ],
}))
</script>
